import { useState, useMemo } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  Box,
  SkeletonThumbnail,
  Text,
  Select,
  ResourceList,
  ResourceItem,
  ChoiceList,
  Filters,
  Bleed,
} from "@medentic/polaris-react";
// import { SearchIcon, XSmallIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { Event, EventStatusType } from "@features/events/types";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import { RenderDate } from "@shared/ui";
import { formatStartDate } from "@shared/lib";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { getLocalizedText } from "@shared/lib";
import { resolveSelectedRelation } from "./relatedEventSelectionModel";

interface SelectRelatedEventsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (eventIds: string[], relation: string) => void;
  existingRelatedEventIds: string[];
  preselectedRelation?: string;
  eventIdsInOtherRelations?: string[];
}

export default function SelectRelatedEventsModal({
  isOpen,
  onClose,
  onAdd,
  existingRelatedEventIds,
  eventIdsInOtherRelations,
  preselectedRelation = "",
}: SelectRelatedEventsModalProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc, eventId: currentEventId } = useParams<{
    eventOrganizerId: string;
    eventId: string;
  }>();
  const { metadata } = useEventMetadata();
  const { GET } = useApiRequest();
  const [selectedRelationOverride, setSelectedRelationOverride] = useState<
    string | null
  >(null);
  const [selectedEventsOverride, setSelectedEventsOverride] = useState<
    string[] | null
  >(null);
  const [queryValue, setQueryValue] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<EventStatusType[]>([
    EventStatusType.ONGOING,
    EventStatusType.UPCOMING,
  ]);

  // Wrapper function to handle type conversion for ChoiceList
  const handleStatusChange = (selected: string[]) => {
    setSelectedStatuses(selected as EventStatusType[]);
  };

  const selectedEvents = selectedEventsOverride ?? existingRelatedEventIds ?? [];

  const toggleSelection = (eventId: string) => {
    if (selectedEvents.includes(eventId)) {
      setSelectedEventsOverride(selectedEvents.filter((id) => id !== eventId));
    } else {
      setSelectedEventsOverride([...selectedEvents, eventId]);
    }
  };

  // Fetch events
  const { data: allEvents = [], isLoading: eventsLoading } = useQuery<Event[]>({
    queryKey: ["events", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/events?`),
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });

  const { metadata: { eventStatus = [] } = {} } = useEventMetadata();
  const statusOptions = useMemo(
    () =>
      eventStatus?.map((status) => ({
        label: status.label,
        value: status.slug,
      })) || [],
    [eventStatus]
  );

  // Filter events based on search and status
  const filteredEvents = useMemo(() => {
    let events = allEvents.filter(
      (event) =>
        event.id !== currentEventId &&
        !eventIdsInOtherRelations?.includes(event.id)
    );

    if (selectedStatuses.length > 0) {
      const set = new Set(selectedStatuses.map((s) => s.toLowerCase()));
      events = events.filter(
        (e) =>
          (e.status || "").toLowerCase() &&
          set.has((e.status || "").toLowerCase())
      );
    }

    if (queryValue) {
      events = events.filter((event) => {
        const title = getLocalizedText(event.title);
        return title.toLowerCase().includes(queryValue.toLowerCase());
      });
    }

    return events;
  }, [
    allEvents,
    queryValue,
    selectedStatuses,
    currentEventId,
    eventIdsInOtherRelations,
  ]);

  const relationOptions = useMemo(() => {
    return (
      metadata?.eventRelations?.map((relation) => ({
        label: relation.label,
        value: relation.slug,
      })) || []
    );
  }, [metadata?.eventRelations]);

  const selectedRelation = resolveSelectedRelation({
    relationOptions,
    selectedRelationOverride,
    preselectedRelation,
  });

  const handleLink = () => {
    if (selectedEvents.length > 0 && selectedRelation) {
      onAdd(selectedEvents, selectedRelation);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedEventsOverride(null);
    setSelectedRelationOverride(null);
    setQueryValue("");
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={t("eventDetails.relatedEvents.selectEventsToLink")}
      primaryAction={{
        content: t("eventDetails.relatedEvents.link"),
        onAction: handleLink,
        disabled: selectedEvents.length === 0 || !selectedRelation,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleClose,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
      footer={
        !preselectedRelation && (
          <InlineStack>
            <Select
              label=""
              options={relationOptions}
              value={selectedRelation}
              onChange={(value) => setSelectedRelationOverride(value)}
            />
          </InlineStack>
        )
      }
    >
      <Modal.Section>
        <Bleed marginInline="400" marginBlock="400">
          {/* Events List with integrated filtering */}
          {eventsLoading ? (
            <Box padding="400">
              <BlockStack gap="200">
                {[1, 2, 3].map((i) => (
                  <InlineStack key={i} gap="200" blockAlign="center">
                    <Box width="20px" minHeight="20px" />
                    <Box minHeight="20px">
                      <SkeletonThumbnail size="small" />
                    </Box>
                    <BlockStack gap="100">
                      <Box
                        width="200px"
                        background="bg-surface"
                        minHeight="16px"
                      />
                      <Box
                        width="120px"
                        background="bg-surface"
                        minHeight="12px"
                      />
                    </BlockStack>
                  </InlineStack>
                ))}
              </BlockStack>
            </Box>
          ) : (
            <ResourceList
              resourceName={{ singular: "event", plural: "events" }}
              items={filteredEvents}
              selectedItems={selectedEvents}
              onSelectionChange={(selection) =>
                setSelectedEventsOverride(selection as string[])
              }
              selectable
              filterControl={
                <Filters
                  queryValue={queryValue}
                  queryPlaceholder={t("eventsList.searchPlaceholder")}
                  onQueryChange={setQueryValue}
                  onQueryClear={() => setQueryValue("")}
                  closeOnChildOverlayClick
                  filters={[
                    {
                      key: "status",
                      label: t("eventsList.status"),
                      filter: (
                        <ChoiceList
                          title={t("eventsList.status")}
                          choices={[...statusOptions]}
                          selected={selectedStatuses}
                          onChange={handleStatusChange}
                          allowMultiple
                        />
                      ),
                      shortcut: true,
                    },
                  ]}
                  appliedFilters={
                    selectedStatuses.length > 0
                      ? [
                          {
                            key: "status",
                            label: `${t(
                              "eventsList.status"
                            )} is ${selectedStatuses
                              .map(
                                (s) =>
                                  statusOptions.find(
                                    (option) => option.value === s
                                  )?.label
                              )
                              .join(" and ")}`,
                            onRemove: () => setSelectedStatuses([]),
                          },
                        ]
                      : []
                  }
                  onClearAll={() => {
                    setQueryValue("");
                    setSelectedStatuses([]);
                  }}
                />
              }
              emptyState={
                <Box padding="400">
                  <Text as="span" variant="bodySm" tone="subdued">
                    {t("eventDetails.relatedEvents.noMoreEvents")} asdasadsadsa
                  </Text>
                </Box>
              }
              renderItem={(event: Event) => {
                const title = getLocalizedText(event.title);
                return (
                  <ResourceItem
                    id={event.id}
                    accessibilityLabel={`Select ${title}`}
                    onClick={() => toggleSelection(event.id)}
                    verticalAlignment="center"
                  >
                    <InlineStack
                      align="space-between"
                      blockAlign="center"
                      wrap={false}
                    >
                      <BlockStack gap="100">
                        <Text
                          as="h3"
                          variant="bodyMd"
                          fontWeight="bold"
                          breakWord
                        >
                          {title}
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {event.dateStart === event.dateEnd &&
                          event.dateStart ? (
                            <RenderDate
                              dayFirst={true}
                              date={event.dateStart}
                            />
                          ) : (
                            <>
                              {formatStartDate(event.dateStart)}
                              {event.dateEnd && (
                                <>
                                  {" "}
                                  -{" "}
                                  <RenderDate
                                    dayFirst={true}
                                    date={event.dateEnd}
                                  />
                                </>
                              )}
                            </>
                          )}
                        </Text>
                      </BlockStack>
                      <EventBadgeRenderer
                        slug={event.status ?? ""}
                        entity="event"
                      />
                    </InlineStack>
                  </ResourceItem>
                );
              }}
            />
          )}
        </Bleed>
      </Modal.Section>
    </Modal>
  );
}
